import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";
import { AuthProvider } from "@/contexts/AuthContext";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { TrialGateProvider } from "@/hooks/useTrialGate";
import { SupportProvider } from "@/components/support/SupportContext";
import { AdminModeProvider } from "@/contexts/AdminModeContext";
import { ThemeProvider } from "next-themes";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useRoutePreload } from "@/hooks/useRoutePreload";
import { usePerformanceMonitor } from "@/hooks/usePerformanceMonitor";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { AppRoutes } from "@/routes";

// Lazy-load non-critical global UI components to reduce initial bundle
const AdminModeIndicator = lazy(() => import("@/components/admin/AdminModeIndicator").then(m => ({ default: m.AdminModeIndicator })));
const SupportAIButton = lazy(() => import("@/components/SupportAIButton"));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
const BackToTop = lazy(() => import("@/components/BackToTop"));
const BetaFeedbackWidget = lazy(() => import("@/components/beta/BetaFeedbackWidget").then(m => ({ default: m.BetaFeedbackWidget })));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

const PerformanceInitializer = () => {
  useRoutePreload();
  usePerformanceMonitor();
  useNetworkStatus();
  return null;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem storageKey="nodistar-theme">
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <SubscriptionProvider>
                <OrganizationProvider>
                  <AdminModeProvider>
                    <TrialGateProvider>
                      <SupportProvider>
                      <PerformanceInitializer />
                      <Suspense fallback={null}>
                        <SupportAIButton />
                        <CookieConsent />
                        <BackToTop />
                        <BetaFeedbackWidget />
                        <AdminModeIndicator />
                      </Suspense>

                      <ErrorBoundary>
                        <AppRoutes />
                      </ErrorBoundary>
                      </SupportProvider>
                    </TrialGateProvider>
                  </AdminModeProvider>
                </OrganizationProvider>
              </SubscriptionProvider>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
